import React, { useContext, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from 'containers/authentication/authenticationSlice'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import useGetOrganizations from 'containers/organizations/useGetOrganizations'
import ScreenLoading from 'components/ScreenLoading'
import DropDownField from 'components/DropDownField'
import { MCP_DOCS_URL } from 'constants/mcp'
import api from 'containers/api'

const McpSettingsTab = () => {
  const { currentOrganization } = useContext(OrganizationsContext)
  const { organizations: allOrganizations, getOrganizations } = useGetOrganizations()
  const token = useSelector(selectToken)
  const [pendingRequest, setPendingRequest] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  const organizations = allOrganizations || (currentOrganization ? [currentOrganization] : [])
  const [selectedOrgId, setSelectedOrgId] = useState('')

  useEffect(() => {
    getOrganizations()
    fetchMcpConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchMcpConfig = async () => {
    try {
      setLoading(true)
      const data = await api.getMCPConfig({ token })
      if (data.organization_id) {
        setSelectedOrgId(data.organization_id.toString())
      } else if (currentOrganization) {
        setSelectedOrgId(currentOrganization.id.toString())
      }
    } catch (err) {
      console.error('Error fetching MCP config:', err)
      // If no config exists, default to current organization
      if (currentOrganization) {
        setSelectedOrgId(currentOrganization.id.toString())
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setPendingRequest(true)
    setSaveSuccess('')
    setError('')

    try {
      await api.updateMCPConfig({
        organizationId: parseInt(selectedOrgId),
        token
      })
      setSaveSuccess('MCP settings updated successfully')
    } catch (err) {
      console.error('Error updating MCP settings:', err)
      setError('Failed to update MCP settings')
    } finally {
      setPendingRequest(false)
    }
  }

  return (
    <div style={{ marginTop: '1rem', width: '100%', maxWidth: '700px', padding: '0 1rem', boxSizing: 'border-box' }}>
      <div
        style={{
          padding: '1rem',
          backgroundColor: '#d1ecf1',
          border: '1px solid #bee5eb',
          borderRadius: '4px',
          marginBottom: '2rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="material-icons-outlined" style={{ color: '#0c5460' }}>
            info
          </span>
          <strong style={{ color: '#0c5460' }}>About MCP Integration</strong>
        </div>
        <p style={{ margin: '0.5rem 0 0 0', color: '#0c5460', fontSize: '0.9rem', lineHeight: '1.6', wordBreak: 'break-word' }}>
          MCP (Model Context Protocol) allows AI assistants to access your Maya Insights
          data. Select which organization's data should be accessible through MCP.
        </p>
      </div>

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: '1.5rem' }}>
          <DropDownField
            label="Default Organization for MCP Access"
            options={[
              { label: 'Select an organization...', value: '' },
              ...organizations.map((org) => ({ label: org.name, value: org.id.toString() }))
            ]}
            value={selectedOrgId}
            onChange={setSelectedOrgId}
            style={{ marginBottom: '0.5rem' }}
          />
          <small style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
            This organization's data will be accessible when you connect through MCP
          </small>
        </div>

        {error && <p className="form-error">{error}</p>}
        {saveSuccess && <p className="form-success">{saveSuccess}</p>}

        <button
          type="submit"
          className="submit-button"
          disabled={!selectedOrgId || pendingRequest}
          style={{ marginTop: '1rem', minWidth: '140px' }}
        >
          {pendingRequest ? <ScreenLoading spinnerColor="white" /> : 'Save Settings'}
        </button>
      </form>

      <div
        style={{
          marginTop: '3rem',
          padding: '1.5rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
        }}
      >
        <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="material-icons-outlined">security</span>
          Current Configuration
        </h4>
        <div style={{ fontSize: '0.9rem', color: '#6c757d', lineHeight: '1.8' }}>
          <p style={{ wordBreak: 'break-word' }}>
            <strong>Selected Organization:</strong>{' '}
            {selectedOrgId
              ? organizations.find((org) => org.id.toString() === selectedOrgId)?.name || 'Unknown'
              : 'None'}
          </p>
          <p style={{ marginTop: '1rem', wordBreak: 'break-word' }}>
            <strong>MCP Status:</strong>{' '}
            <span style={{ color: '#28a745', fontWeight: 600 }}>Active</span>
          </p>
        </div>
      </div>

      <div
        style={{
          marginTop: '2rem',
          padding: '1.5rem',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
        }}
      >
        <h4 style={{ marginTop: 0, wordBreak: 'break-word' }}>Learn More About MCP</h4>
        <p style={{ fontSize: '0.9rem', color: '#6c757d', lineHeight: '1.6', wordBreak: 'break-word' }}>
          MCP enables secure, controlled access to your data through AI assistants. You can
          revoke access at any time from your account settings.
        </p>
        <a
          href={MCP_DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1rem',
            color: '#007bff',
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
        >
          Read MCP Documentation
          <span className="material-icons-outlined" style={{ fontSize: '1.2rem' }}>
            open_in_new
          </span>
        </a>
      </div>
    </div>
  )
}

export default McpSettingsTab
